import { makeStyles } from '@material-ui/core/styles'
import classnames from 'classnames'
import React from 'react'
import { useTopScrollbar } from '../../hooks/useTopScrollbar'
import type { StyleClasses } from '../../styles'

const useStyles = makeStyles(
  {
    root: {},
    // The proxy: an empty scroll container whose only job is to show a horizontal scrollbar above
    // the content. `aria-hidden` because it duplicates a scroll affordance the content already has;
    // `tabIndex={-1}` because a scrollable box is focusable in some browsers, and a focusable
    // aria-hidden element fails the aria-hidden-focus rule.
    scrollbar: {
      overflowX: 'auto',
      overflowY: 'hidden'
    },
    spacer: {
      height: 1
    },
    // `overflow-y: hidden` is declared deliberately rather than left to the default. These tables
    // previously scrolled on a MUI `Card`, which ships `overflow: hidden`, so the used value there
    // was `overflow-x: auto / overflow-y: hidden`. A bare `overflow-x: auto` on a plain div would
    // instead compute `overflow-y: auto`, changing scroll anchoring and the scrollport that
    // `position: sticky` descendants resolve against.
    content: {
      overflowX: 'auto',
      overflowY: 'hidden',
      '-webkit-overflow-scrolling': 'touch'
    },
    // Hide the scrolling element's own bar so only the proxy above is visible — but only on
    // platforms where a classic scrollbar reserves layout space. Where scrollbars are overlays the
    // proxy is unusable (it would be painted over a 1px strip), so the content keeps its own bar.
    contentScrollbarHidden: {
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': {
        display: 'none'
      }
    }
  },
  { name: 'TopScrollbar' }
)

export interface TopScrollbarProps {
  classes?: StyleClasses<typeof useStyles>
  /** Applied to the outer wrapper. */
  className?: string
  /** Applied to the scrolling element, alongside the internal `content` class. */
  contentClassName?: string
  /**
   * Render `children` bare — no wrapper, no scrolling of our own — so a caller that only wants the
   * top scrollbar conditionally produces exactly the markup it had before. `className` and
   * `contentClassName` are ignored in this mode.
   */
  disabled?: boolean
  children: React.ReactNode
}

/**
 * Renders `children` in a horizontally-scrolling box whose scrollbar sits *above* it rather than
 * below, so it stays reachable without scrolling to the bottom of a tall table.
 *
 * Replaces the `transform: scaleY(-1)` + counter-flipped children trick that used to achieve this:
 * that flip pushed the subtree onto a composited layer and made its text render visibly soft on
 * desktop (#21853). See {@link useTopScrollbar}.
 */
export default function TopScrollbar({ className, contentClassName, disabled, children, ...props }: TopScrollbarProps) {
  const classes = useStyles(props)
  const { scrollbarRef, contentRef, contentWidth, hasOverflow, scrollbarThickness } = useTopScrollbar(!disabled)

  if (disabled) {
    return <>{children}</>
  }

  const hasClassicScrollbars = scrollbarThickness > 0
  const showProxy = hasOverflow && hasClassicScrollbars

  return (
    <div className={classnames(classes.root, className)}>
      <div
        ref={scrollbarRef}
        className={classes.scrollbar}
        aria-hidden
        tabIndex={-1}
        style={{ display: showProxy ? 'block' : 'none' }}
      >
        <div className={classes.spacer} style={{ width: contentWidth }} />
      </div>
      <div
        ref={contentRef}
        className={classnames(
          classes.content,
          hasClassicScrollbars && classes.contentScrollbarHidden,
          contentClassName
        )}
      >
        {children}
      </div>
    </div>
  )
}
